/* eslint-disable indent */
import { NavLink } from 'react-router-dom'

import { LogoStore } from '~/components/LogoStore'
import ProgressBar from '~/components/ProgressBar'
import Tooltip from '~/components/Tooltip'
import { Badge } from '~/components/UI/Badge'
import { formatMoneyV2 } from '~/helpers'
import i18n from '~/i18n'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'

import { ImpressionShareMetricCell } from '../_elements/ImpressionShareMetricCell'
import {
  haloColumns,
  buildClickAttributionColumns
} from '../_elements/roasColumns'
import { MetricTooltip } from '../../MetricMethodologyTooltip'

const { t } = i18n

type SchemaProps = {
  availableMetrics?: NewMetricsAvailability
  showClickAlert?: boolean
}

export const schema = ({
  availableMetrics = {
    impressionShare: false,
    lostIsRank: false,
    lostIsBudget: false,
    newToBrandCustomers: false,
    newToBrandRevenue: false
  },
  showClickAlert = false
}: SchemaProps = {}) => {
  /**
   * Colunas de atribuição (Halo + ROAS por clique) são compartilhadas e tipadas
   * para AdsResultFormatted. Adaptamos ao tipo da tabela de advertisers para
   * referenciá-las individualmente na ordem desejada.
   */
  const attributionColumns = {
    ...haloColumns,
    ...buildClickAttributionColumns(showClickAlert)
  } as unknown as TableSchema<AdvertiserWithMetrics>

  return {
    title: {
      title: t('rm:Advertiser'),
      sortKey: 'name',
      render: rowData => (
        <NavLink
          to={`/retail-media/campaigns?rmid=${rowData.id}`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <LogoStore
            alt={rowData.title}
            linkLogo={rowData.logo_url}
            backgroundLogo={
              rowData.account_theme?.header_color || 'transparent'
            }
            size="small"
          />
          <div style={{ lineHeight: 1, marginLeft: '.5rem' }}>
            {rowData?.sellerId && (
              <div className="w-100">
                <Badge title="Seller ID" size="tiny">
                  Seller ID: {rowData.sellerId}
                </Badge>
              </div>
            )}

            <strong className="fs-6">{rowData.title}</strong>

            <div className="w-100">
              {rowData?.advertiserTags?.map(tag => (
                <Badge
                  key={tag.id}
                  title="Tag do anunciante"
                  size="tiny"
                  text={tag.label}
                  template="primary"
                />
              ))}
            </div>
          </div>
        </NavLink>
      ),
      className: 'order__details col__infos col__title',
      showTotal: true
    },
    balance: {
      title: t('rm:balanceInWallet'),
      sortKey: 'balance',
      sortInitialToggleDirection: 'DESC',
      render: rowData => (
        <div style={{ lineHeight: 1.125 }}>
          {rowData?.balance}
          {rowData?.checkingAccountId && (
            <>
              <br />
              <NavLink
                to={`/retail-media/wallet/${rowData.checkingAccountId}?rmid=${rowData.id}`}
              >
                <small>{t('rm:accessWallet')}</small>
              </NavLink>
            </>
          )}
        </div>
      )
    },
    daily_budget: {
      title: t('rm:dailyBudget'),
      sortKey: 'total_daily_budget',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) =>
        metrics?.totalDailyBudget ? (
          <div className="d-flex flex-column gap-1">
            <span className="fs-6">{metrics.totalDailyBudget}</span>

            <Tooltip
              noMargin
              icon={
                <ProgressBar
                  style={{ width: '7rem' }}
                  goal={Number(metrics.raw.total_daily_budget)}
                  value={Number(metrics.raw.consumed_daily_budget)}
                />
              }
              text={`${formatMoneyV2({ value: metrics.raw.consumed_daily_budget })} — ${formatMoneyV2(
                {
                  value: metrics.raw.total_daily_budget
                }
              )}`}
            />
          </div>
        ) : (
          '-'
        )
    },
    actives_campaigns: {
      title: t('rm:Campaigns'),
      optional: true,
      sortKey: 'total_campaigns',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.totalCampaigns || '-'
    },
    impressions: {
      title: t('rm:metrics.impressions'),
      helpText: t('rm:metrics.impressions.tooltip'),
      sortKey: 'impressions',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.impressions || '-'
    },
    views: {
      title: t('rm:metrics.views'),
      helpText: t('rm:metrics.views.tooltip'),
      sortKey: 'views',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.views || '-'
    },
    clicks: {
      title: t('rm:metrics.clicks'),
      helpText: t('rm:metrics.clicks.tooltip'),
      sortKey: 'clicks',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.clicks || '-'
    },
    ctr: {
      title: t('rm:metrics.ctr'),
      helpContent: <MetricTooltip metric="ctr" />,
      optional: true,
      sortKey: 'ctr',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.ctr || '-'
    },
    total_spent: {
      title: t('rm:metrics.adsRevenue'),
      helpText: t('rm:metrics.cost.tooltip'),
      sortKey: 'total_spent',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.totalSpent || '-'
    },
    conversion: {
      title: t('rm:metrics.conversions'),
      headerSuffix: showClickAlert ? <ConversionsCutoffAlertIcon /> : undefined,
      helpText: t('rm:metrics.conversions.tooltip'),
      sortKey: 'conversions',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.conversions || '-'
    },
    conversionsClick: {
      ...attributionColumns.conversionsClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionsClick" />
    },
    conversionRate: {
      title: t('rm:metrics.conversion_rate'),
      headerSuffix: showClickAlert ? (
        <ConversionRateCutoffAlertIcon />
      ) : undefined,
      helpContent: (
        <MetricTooltip
          metric="conversion_rate"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.conversionUpdate.link')}
        />
      ),
      sortKey: 'conversion_rate',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.conversionRate || '-'
    },
    conversionRateClick: {
      ...attributionColumns.conversionRateClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionRateClick" />
    },
    income: {
      title: t('rm:metrics.income'),
      helpText: t('rm:metrics.sales.tooltip'),
      sortKey: 'income',
      sortInitialToggleDirection: 'DESC',
      render: rowData => rowData?.metrics?.income || '-'
    },
    salesClick: {
      ...attributionColumns.salesClick,
      optional: true,
      helpText: t('rm:metrics.salesClick.tooltip')
    },
    roas: {
      title: t('rm:metrics.roas'),
      helpContent: (
        <MetricTooltip
          metric="roas"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.roasUpdate.link')}
        />
      ),
      sortKey: 'roas',
      sortInitialToggleDirection: 'DESC',
      render: rowData => (
        <RoasMetricCell
          metric="roas"
          value={rowData?.metrics?.roas || '-'}
          raw={rowData?.metrics?.raw}
        />
      )
    },
    roasClick: {
      ...attributionColumns.roasClick,
      optional: true,
      helpContent: <MetricTooltip metric="roasClick" />
    },
    roasHalo: {
      ...attributionColumns.roasHalo,
      optional: true,
      helpContent: <MetricTooltip metric="roasHalo" />
    },
    roasOverall: {
      ...attributionColumns.roasOverall,
      optional: true,
      helpContent: <MetricTooltip metric="roasOverall" />
    },
    haloOrders: {
      ...attributionColumns.haloOrders,
      optional: true,
      helpContent: <MetricTooltip metric="haloOrders" />
    },
    haloRevenue: {
      ...attributionColumns.haloRevenue,
      optional: true,
      helpContent: <MetricTooltip metric="haloRevenue" />
    },
    haloItems: {
      ...attributionColumns.haloItems,
      optional: true,
      helpContent: <MetricTooltip metric="haloItems" />
    },
    ...(availableMetrics.impressionShare
      ? {
          impressionShare: {
            title: t('rm:metrics.impressionShare'),
            helpContent: <MetricTooltip metric="impressionShare" />,
            helpText: t('rm:metrics.impressionShare.tooltip'),
            sortKey: 'impression_share',
            sortInitialToggleDirection: 'DESC',
            optional: true,
            render: rowData => (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.impressionShare}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsRank
      ? {
          lostIsRank: {
            title: t('rm:metrics.lostIsRank'),
            helpContent: <MetricTooltip metric="lostIsRank" />,
            helpText: t('rm:metrics.lostIsRank.tooltip'),
            optional: true,
            sortKey: 'lost_is_rank',
            sortInitialToggleDirection: 'DESC',
            render: rowData => (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.lostIsRank}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsBudget
      ? {
          lostIsBudget: {
            title: t('rm:metrics.lostIsBudget'),
            helpContent: <MetricTooltip metric="lostIsBudget" />,
            helpText: t('rm:metrics.lostIsBudget.tooltip'),
            optional: true,
            sortKey: 'lost_is_budget',
            sortInitialToggleDirection: 'DESC',
            render: rowData => (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.lostIsBudget}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.newToBrandCustomers
      ? {
          newToBrandCustomers: {
            title: t('rm:metrics.newToBrandCustomers'),
            helpText: t('rm:metrics.newToBrandCustomers.tooltip'),
            sortKey: 'new_to_brand_customers',
            sortInitialToggleDirection: 'DESC',
            render: rowData => rowData?.metrics?.newToBrandCustomers || '-'
          }
        }
      : {}),
    ...(availableMetrics.newToBrandRevenue
      ? {
          newToBrandRevenue: {
            title: t('rm:metrics.newToBrandRevenue'),
            helpText: t('rm:metrics.newToBrandRevenue.tooltip'),
            sortKey: 'new_to_brand_revenue',
            sortInitialToggleDirection: 'DESC',
            render: rowData => rowData?.metrics?.newToBrandRevenue || '-'
          }
        }
      : {})
  } satisfies TableSchema<AdvertiserWithMetrics>
}
